import {
  Code2,
  Smartphone,
  Globe,
  PenTool,
  BarChart3,
  Plane,
  Cpu,
  ShieldCheck,
  Radar,
  Bug,
  Megaphone,
  Share2,
  Briefcase,
  BrainCircuit,
} from 'lucide-react'

// Each service powers a card on /services and a full page at /services/:slug.
// Add, remove or edit freely — the UI is generated from this list.
export const services = [
  {
    slug: 'website-development',
    title: 'Website Development',
    icon: Globe,
    category: 'Engineering',
    tagline: 'Fast, modern, SEO-ready websites.',
    summary:
      'Marketing sites, portals and e-commerce stores that load fast and rank well on search.',
    description:
      'We craft responsive, high-performance websites optimised for speed, SEO and conversions — from corporate sites and landing pages to full e-commerce and customer portals.',
    features: [
      'Responsive, mobile-first builds',
      'SEO & Core Web Vitals optimised',
      'E-commerce & payment integration',
      'CMS-driven content you control',
      'Analytics & conversion tracking',
    ],
  },
  {
    slug: 'app-development',
    title: 'App Development',
    icon: Smartphone,
    category: 'Engineering',
    tagline: 'Android, iOS & desktop apps that feel native.',
    summary:
      'Mobile and desktop applications for Android, iOS, Windows and macOS — fast, polished and reliable.',
    description:
      'From a single codebase to fully native builds, we ship mobile and desktop apps your users love. We handle the full lifecycle: design, development, store submission and post-launch updates.',
    features: [
      'Native Android & iOS apps',
      'Cross-platform (React Native / Flutter)',
      'Desktop apps for Windows & macOS',
      'App Store & Play Store deployment',
      'Push notifications & offline support',
    ],
  },
  {
    slug: 'software-development',
    title: 'Software Development',
    icon: Code2,
    category: 'Engineering',
    tagline: 'Custom software engineered around your business.',
    summary:
      'Tailored web platforms, internal tools and enterprise systems built to scale with your operations.',
    description:
      'We design and build custom software from the ground up — from internal dashboards and automation tools to large multi-tenant platforms. Clean architecture, maintainable code and a delivery process that keeps you in the loop at every stage.',
    features: [
      'Custom web & enterprise platforms',
      'API design & system integration',
      'Cloud-native, scalable architecture',
      'Automation & internal tooling',
      'Maintenance & long-term support',
    ],
  },
  {
    slug: 'cyber-security',
    title: 'Cyber Security',
    icon: ShieldCheck,
    category: 'Security',
    tagline: 'Protect your business end to end.',
    summary:
      'Penetration testing, audits, hardening and ongoing security operations.',
    description:
      'We help you find and fix weaknesses before attackers do. From penetration testing and security audits to infrastructure hardening and incident response, we keep your systems and data safe.',
    features: [
      'Penetration testing & VAPT',
      'Security audits & compliance',
      'Infrastructure hardening',
      'Incident response & forensics',
      'Security awareness training',
    ],
  },
  {
    slug: 'ai-automation',
    title: 'AI Automation',
    icon: BrainCircuit,
    category: 'Data & AI',
    tagline: 'Practical AI that ships.',
    summary:
      'LLM apps, chatbots, computer vision and automation tailored to your workflows.',
    description:
      'We build AI solutions that deliver real value — custom chatbots and assistants, document intelligence, computer vision and workflow automation, integrated securely into your products and operations.',
    features: [
      'LLM apps, chatbots & assistants',
      'Document & data intelligence (RAG)',
      'Computer vision solutions',
      'Predictive models & automation',
      'Secure integration with your stack',
    ],
  },
  {
    slug: 'iot-development',
    title: 'IoT Projects',
    icon: Cpu,
    category: 'Hardware',
    tagline: 'Connected devices, sensors & dashboards.',
    summary:
      'End-to-end IoT — firmware, connectivity, cloud ingestion and real-time monitoring.',
    description:
      'We build IoT solutions that connect the physical and digital worlds: embedded firmware, secure device connectivity, cloud data ingestion and dashboards to monitor and control your fleet in real time.',
    features: [
      'Embedded firmware & sensor integration',
      'MQTT / secure device connectivity',
      'Cloud ingestion & device management',
      'Real-time monitoring dashboards',
      'Edge computing & automation',
    ],
  },
  {
    slug: 'ui-ux-design',
    title: 'UI / UX Design',
    icon: PenTool,
    category: 'Design',
    tagline: 'Interfaces people understand instantly.',
    summary:
      'Research-driven product design, wireframes, prototypes and design systems that convert.',
    description:
      'Great products start with great design. We run discovery, map user journeys, and design clean, accessible interfaces — backed by reusable design systems so your product stays consistent as it grows.',
    features: [
      'User research & journey mapping',
      'Wireframes & interactive prototypes',
      'Design systems & component libraries',
      'Accessibility (WCAG) compliance',
      'Usability testing & iteration',
    ],
  },
  {
    slug: 'digital-marketing',
    title: 'Digital Marketing',
    icon: Megaphone,
    category: 'Growth',
    tagline: 'Get found. Get customers.',
    summary:
      'SEO, paid ads and content strategy that drives measurable growth.',
    description:
      'We grow your reach with data-driven marketing — search engine optimisation, paid campaigns, email and content strategy — all tracked against real business goals.',
    features: [
      'SEO & content strategy',
      'Google & social paid ads',
      'Email marketing & automation',
      'Landing pages & funnels',
      'Analytics & ROI reporting',
    ],
  },
  {
    slug: 'business-consulting',
    title: 'Business Consulting',
    icon: Briefcase,
    category: 'Advisory',
    tagline: 'Strategy and technology, aligned.',
    summary:
      'Digital transformation, process optimisation and technology roadmaps.',
    description:
      'We help you make the right technology and business decisions — digital transformation strategy, process optimisation, product roadmaps and hands-on guidance to execute them.',
    features: [
      'Digital transformation strategy',
      'Process & workflow optimisation',
      'Technology & product roadmaps',
      'Cost & efficiency analysis',
      'Go-to-market support',
    ],
  },
  {
    slug: 'social-media-management',
    title: 'Social Media Management',
    icon: Share2,
    category: 'Growth',
    tagline: 'A brand presence that stays active.',
    summary:
      'Content, scheduling, community management and reporting across platforms.',
    description:
      'We keep your brand visible and engaging. From content calendars and creative to community management and analytics, we handle your social presence so you can focus on your business.',
    features: [
      'Content calendar & creative',
      'Scheduling across platforms',
      'Community engagement',
      'Influencer & campaign management',
      'Growth & engagement reporting',
    ],
  },
  {
    slug: 'drone-development',
    title: 'Drone Development',
    icon: Plane,
    category: 'Hardware',
    tagline: 'Aerial systems for inspection, mapping & more.',
    summary:
      'Custom drone hardware, flight software and aerial data solutions for industry.',
    description:
      'We develop drone systems end to end — airframe and payload integration, autonomous flight software, and processing of aerial imagery for mapping, surveying and inspection workflows.',
    features: [
      'Custom airframe & payload integration',
      'Autonomous flight & mission planning',
      'Aerial mapping & photogrammetry',
      'Inspection & surveillance solutions',
      'Telemetry & ground-control software',
    ],
  },

]

export const getService = (slug) => services.find((s) => s.slug === slug)
